import { isIdentifierRecalled, type IdentifierRecord } from '@myplayer/schema';
import {
  formatPlainValidationError,
  resolveIdentifierLifecycleState,
} from './products-firestore.js';

export type NeedsAttentionCategory = 'RECALLED' | 'BROKEN_LINK' | 'UNPUBLISHED_CHANGES';

export interface NeedsAttentionItem {
  readonly identifierKey: string;
  readonly workspaceId?: string;
  readonly workspaceName?: string;
  readonly title: string;
  readonly primaryKey: string;
  readonly category: NeedsAttentionCategory;
  readonly badgeLabel: 'Recalled' | 'Broken link' | 'Has unpublished changes';
  readonly badgeVariant: 'destructive' | 'invalid' | 'warning';
  readonly summary: string;
  readonly actionLabel: 'Manage recall' | 'Fix link' | 'Review changes';
}

export interface NeedsAttentionSummary {
  readonly totalProducts: number;
  readonly publishedCount: number;
  readonly recalledCount: number;
  readonly brokenLinkCount: number;
  readonly unpublishedCount: number;
  readonly items: readonly NeedsAttentionItem[];
}

export function deriveNeedsAttentionItems(
  identifiers: readonly IdentifierRecord[],
  workspaceNameMap?: ReadonlyMap<string, string>,
): NeedsAttentionSummary {
  let publishedCount = 0;
  let recalledCount = 0;
  let brokenLinkCount = 0;
  let unpublishedCount = 0;
  const items: NeedsAttentionItem[] = [];

  for (const record of identifiers) {
    const title = record.title ?? record.draft?.title ?? `Product ${record.primaryKey}`;
    const wsId = record.workspaceId;
    const wsName = wsId ? (workspaceNameMap?.get(wsId) ?? wsId) : undefined;
    const wsFields = {
      ...(wsId !== undefined ? { workspaceId: wsId } : {}),
      ...(wsName !== undefined ? { workspaceName: wsName } : {}),
    };

    if (isIdentifierRecalled(record)) {
      recalledCount += 1;
      const reason = record.recallOverride?.reason?.trim();
      const overrideUrl = record.recallOverride?.overrideUrl?.trim();
      items.push({
        identifierKey: record.identifierKey,
        ...wsFields,
        title,
        primaryKey: record.primaryKey,
        category: 'RECALLED',
        badgeLabel: 'Recalled',
        badgeVariant: 'destructive',
        summary:
          overrideUrl && overrideUrl.length > 0
            ? `${reason ? `${reason} — ` : ''}Redirecting scanners to ${overrideUrl}`
            : `${reason ? `${reason} — ` : ''}Scanners see the emergency recall notice page`,
        actionLabel: 'Manage recall',
      });
      continue;
    }

    const lifecycle = resolveIdentifierLifecycleState(record);
    if (lifecycle === 'invalid') {
      brokenLinkCount += 1;
      const firstErr = record.validation?.errors?.[0];
      items.push({
        identifierKey: record.identifierKey,
        ...wsFields,
        title,
        primaryKey: record.primaryKey,
        category: 'BROKEN_LINK',
        badgeLabel: 'Broken link',
        badgeVariant: 'invalid',
        summary: firstErr
          ? formatPlainValidationError(firstErr)
          : 'Destination web address failed safety or check-digit validation.',
        actionLabel: 'Fix link',
      });
      continue;
    }

    if (lifecycle === 'draft' || lifecycle === 'pending') {
      unpublishedCount += 1;
      const targetUrl =
        record.draft?.defaultUrl ??
        record.draft?.links?.find((l) => l.defaultLink)?.href ??
        record.defaultUrl ??
        'No destination link set yet';
      items.push({
        identifierKey: record.identifierKey,
        ...wsFields,
        title,
        primaryKey: record.primaryKey,
        category: 'UNPUBLISHED_CHANGES',
        badgeLabel: 'Has unpublished changes',
        badgeVariant: 'warning',
        summary:
          lifecycle === 'draft'
            ? `Not published yet — scanners will go to ${targetUrl} once published`
            : `Staged changes waiting to publish (${targetUrl})`,
        actionLabel: 'Review changes',
      });
      continue;
    }

    publishedCount += 1;
  }

  const categoryWeight: Readonly<Record<NeedsAttentionCategory, number>> = {
    RECALLED: 0,
    BROKEN_LINK: 1,
    UNPUBLISHED_CHANGES: 2,
  };
  items.sort((a, b) => {
    const diff = categoryWeight[a.category] - categoryWeight[b.category];
    return diff !== 0 ? diff : a.identifierKey.localeCompare(b.identifierKey);
  });

  return {
    totalProducts: identifiers.length,
    publishedCount,
    recalledCount,
    brokenLinkCount,
    unpublishedCount,
    items,
  };
}
